import '../../stylesheets/dashboards/dashboards.css'
import '../../stylesheets/dashboards/transaction-comps.css'
/* == React == */
import { useState, useEffect, useRef }                                      from 'react'
import { useOutletContext }                                                 from 'react-router-dom'
/* == Components == */
import ChartsSkeleton                                                       from '../skeletons/ChartsSkeleton.js'
import MetricPaletteSections                                                from './benchmarking-charts-shared/MetricPaletteSections.js'
import Chart                                                                from 'chart.js/auto';
import ChartDataLabels                                                      from 'chartjs-plugin-datalabels';
/* == Contstants == */
import { MINI_IPAD }                                                        from '../../_constants/break-points.js';
import { SELECTION_UPPER_LIMIT, TC_TOOLTIP_TILE_COMPANION_DICTIONARY,
         Y_AXIS_GUTTER_PX, Y_AXIS_GUTTER_MOBILE_PX }                        from '../../_constants/transaction-comps.js';
import { TRANS_TILE_SECTIONS_LIST }                                         from '../../_constants/transaction-comps.js';         
/* == Hooks / Handlers == */
import { usePublicAPI }                                                     from '../../hooks/external/proprietary/usePublicAPI.js';
import { useViewportWidth }                                                 from '../../hooks/internal/ui/useViewportWidth.js'
/* == Services == */
import { createIdColorDictionary, chartColorsService }                      from '../../services/benchmarkCharts/main/main.js';
import { orchestrateCompanion, orchestrateYAxis,
         orchestrateFooterCompanion }                                       from '../../services/benchmarkCharts/main/main.js'
/* == Utilities == */
import { filterManyByIds, findOneById }                                     from '../../utilities/idResolution.js';
import { checkViewportWidthLessThan }                                       from '../../utilities/viewport.js';
import { median }                                                           from '../../utilities/statistics.js'
import { parseToNumericOnly }                                               from '../../utilities/sanitize.js';

// Register the datalabels plugin
Chart.register(ChartDataLabels);

const TransactionComps = () => {
    // ====== Hooks ====== //
    const viewportWidth = useViewportWidth();

    // ====== State ====== //
    // Lifted React State
    const {
        // Transactions (Read Only)
        transCategories,
        selectedTransCategoryId,
        onTransCategorySelectionChange,
        transactions,
        selectedTransactionIds,
        onTransactionSelectionChange,
        transTiles,
        selectedTransTileIds,
        onTransTileSelectionChange,
        transactionsIsLoading
    } = useOutletContext()

    const { getCoLogo } = usePublicAPI()

    // Local state
    const [draggedTileId, setDraggedTileId]     = useState(null);
    const [draggedFromTray, setDraggedFromTray] = useState(null);
    const [ghostPosition, setGhostPosition]     = useState(null); // Track touch coordinates for ghost element
    const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
    const [searchTerm, setSearchTerm]           = useState('');
    const [isDropdownOpen, setIsDropdownOpen]   = useState(false);

    const chart1Ref                             = useRef(null);
    const chart2Ref                             = useRef(null);
    const chart1Instance                        = useRef(null);
    const chart2Instance                        = useRef(null);

    // Local Variables
    const isViewportWidthMobileBrowser          = checkViewportWidthLessThan(viewportWidth, MINI_IPAD)                    // returns value of true or false into variable declaration to use to ternary for two way branch conditional render of different font size, label rotation, etc in chart config object for chart build
    const idColorDictionary                     = createIdColorDictionary(selectedTransactionIds);

    const updateCharts = () => {
        if (selectedTransTileIds[0]) {
            const tile1 = findOneById(transTiles, selectedTransTileIds[0]);
            createChart(chart1Ref, chart1Instance, tile1, transactions);
        }
        if (selectedTransTileIds[1]) {
            const tile2 = findOneById(transTiles, selectedTransTileIds[1]);
            createChart(chart2Ref, chart2Instance, tile2, transactions);
        }
    };

    // ====== Logic Begin ====== //
    useEffect(() => {
        // Update charts when transactions or tiles change
        updateCharts();
    }, [selectedTransactionIds, selectedTransTileIds, isViewportWidthMobileBrowser]);

    if (transactionsIsLoading) return <ChartsSkeleton />

    const handleDragStart = (e, tileId, fromTrayIndex = null) => {
        setDraggedTileId(tileId);
        setDraggedFromTray(fromTrayIndex);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e) => {
        e.preventDefault();
        e.currentTarget.classList.add('drag-over');
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleTrayDrop = (e, index) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        
        if (draggedTileId) {
            const newSelectedTiles = [...selectedTransTileIds];
            
            if (draggedFromTray !== null && draggedFromTray !== index) {
                const existingTileId = newSelectedTiles[index];
                newSelectedTiles[index] = draggedTileId;
                newSelectedTiles[draggedFromTray] = existingTileId;
                onTransTileSelectionChange(newSelectedTiles);
            }
            else if (draggedFromTray === null) {
                newSelectedTiles[index] = draggedTileId;
                onTransTileSelectionChange(newSelectedTiles);
            }
            // else: Dragging from tray back to same slot (no-op, so don't call state setter)
            
            setDraggedTileId(null);
            setDraggedFromTray(null);
        }
    };

    const handlePaletteDrop = (e) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag-over');
        
        if (draggedTileId && draggedFromTray !== null) {
            const newSelectedTiles = [...selectedTransTileIds];
            newSelectedTiles[draggedFromTray] = null;
            onTransTileSelectionChange(newSelectedTiles);
        }
        
        setDraggedTileId(null);
        setDraggedFromTray(null);
    };

    // ** Touch event handlers called by touch event listeners - exactly match mouse handler logic **
    const handleTouchStart = (e, tileId, fromTrayIndex = null) => {
        // Canonical place for setting initial value of "tracking" state variables on the source side
        setDraggedTileId(tileId);
        setDraggedFromTray(fromTrayIndex);
        // Initialize ghost position immediately at touch start point
        const touch = e.touches[0];
        setGhostPosition({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchMove = (e) => {
        // Canonical place for editing /overriding browser infra and UI/dynamic visual feedback updates
        e.preventDefault(); // Prevent scrolling while dragging
        if (draggedTileId) {
            const touch = e.touches[0];
            setGhostPosition({ x: touch.clientX, y: touch.clientY });
        }
    };

    const handleTouchEnd = (e) => {
        e.preventDefault();
        
        // Early exit if nothing is being dragged
        if (!draggedTileId) {
            return;
        }
        
        // Get the touch point coordinates on DOM from exact point where user lifted finger
        const touch = e.changedTouches[0];
        
        // Find which DOM element is at those exact coordinates
        // This is how touch detects "drop targets" (no automatic detection like mouse drag-and-drop)
        const dropTargetElement = document.elementFromPoint(touch.clientX, touch.clientY);
        
        // If no element found (finger lifted outside browser), just reset state and early return
        if (!dropTargetElement) {
            setDraggedTileId(null);
            setDraggedFromTray(null);
            setGhostPosition(null);
            return;
        }
        
        // Detect which drop zone the tile was dropped on
        const componentTraySlot = dropTargetElement.closest('.component-tray');
        const metricPaletteContainer = dropTargetElement.closest('.tiles-grid');
        
        // Case 1: Dropped on component tray slot
        if (componentTraySlot) {
            const componentTrayTargetIndex = parseInt(componentTraySlot.getAttribute('data-tray-index'));
            
            if (componentTrayTargetIndex === 0 || componentTrayTargetIndex === 1) {
                const newSelectedTiles = [...selectedTransTileIds];
                
                // Scenario A: Swap tiles between tray slots
                if (draggedFromTray !== null && draggedFromTray !== componentTrayTargetIndex) {
                    const existingTileId = newSelectedTiles[componentTrayTargetIndex];
                    newSelectedTiles[componentTrayTargetIndex] = draggedTileId;
                    newSelectedTiles[draggedFromTray] = existingTileId;
                    onTransTileSelectionChange(newSelectedTiles);
                }
                // Scenario B: Add tile from palette to tray
                else if (draggedFromTray === null) {
                    newSelectedTiles[componentTrayTargetIndex] = draggedTileId;
                    onTransTileSelectionChange(newSelectedTiles);
                }
                // Scenario C: Dropped on same tray slot (no-op, don't call state setter)
            }
        }
        
        // Case 2: Dropped in metric palette container
        if (metricPaletteContainer) {
            // Only remove tile from tray if tile came from a tray (not palette-to-palette)
            if (draggedFromTray !== null) {
                const newSelectedTiles = [...selectedTransTileIds];
                newSelectedTiles[draggedFromTray] = null;
                onTransTileSelectionChange(newSelectedTiles);
            }
        }
        
        // Case 3: Dropped somewhere else (invalid drop zone)
        // Cleanup: Always reset drag state after touch ends
        setDraggedTileId(null);
        setDraggedFromTray(null);
        setGhostPosition(null);
    };

    const createChart = (canvasRef, chartInstanceRef, tile, transactions) => {
        if (!canvasRef.current || !tile || transactions.length === 0) return;

        const ctx = canvasRef.current.getContext('2d');
        
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }

        // **---------- Sort selectedCompanyIds by metric value (highest to lowest) with N/A's last ----------** //
        const orderedIds = [...selectedTransactionIds].sort((a, b) => {
            const txnA = findOneById(transactions, a);
            const txnB = findOneById(transactions, b);
            return txnB[tile.field] - txnA[tile.field];
        });        

        // **---------- Company Native Values of Charted Metric (Tile Field) w/ N/A's Preserved  ----------** // 
        const dbValuesForField = orderedIds.map(id => {
            const transaction = findOneById(transactions, id);
            return transaction[tile.field]; // return the value of a field from that company object with id matching the id (element) we are currently on in loop over array 
        });
        
        // **---------- For actual data we chart - must Convert 'N/A' values to 0 so bars visually display ----------** //
        // [NOTE 1]: For actual data we chart - must Convert 'N/A' values to 0 so bars visually display (while still allowing labels/tooltips use dbValuesForField to show 'N/A' gracefully!)
        // [NOTE 2]: Chart.js only renders bars for numeric data types. If we passed 'N/A' strings directly, those companies would have no bar at all. By converting 'N/A' -> 0, we ensure a bar renders (at 0 height) so the company stays visible with its 'N/A' label and tooltip.        
        const data = dbValuesForField.map(value => value === 'N/A' ? 0 : value)
        // [NOTE 3]: Required to be in config for chart js library to work, but not actually used in what we visually render: see top level data prop below, and then nested 'labels' prop in chart config object
        const labels = orderedIds.map(id => {
            const txn = findOneById(transactions, id);
            return `${txn.acquirerTicker} -> ${txn.targetTicker}`;
        })

        chartInstanceRef.current = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [
                    {
                        label: tile.name,
                        data: data,
                        backgroundColor: chartColorsService(idColorDictionary, orderedIds),
                        borderWidth: 0,
                        borderRadius: 4,
                        borderSkipped: false,
                        order: 0,
                    },
                    {
                        label: 'Median',
                        type: 'line',
                        data: Array(selectedTransactionIds.length).fill( median( parseToNumericOnly(dbValuesForField)) ),
                        datalabels: { display: false },   // ← add; hides labels for this dataset only                        
                        borderColor: 'rgba(101, 124, 178, 0.8)',
                        borderWidth: 3,
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        order: 1
                    },
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    title: {
                        display: false,
                    },
                    legend: { 
                        display: false 
                    },
                    datalabels: {
                        color: '#666',
                        font: {
                          size: isViewportWidthMobileBrowser ? 10 : 11,
                          family: 'Arial'
                        },
                        anchor: "end",
                        align: "end",
                        formatter: (value, context) => {
                            const percentageDbFields = ["ttmRevenueGrowth", "ttmEbitdaMargin", "premiumPaid"]
                            const amountDbFields = ["dealSize"]
                            
                            // Get original value from dbValuesForField (preserves N/A)
                            const originalValue = dbValuesForField[context.dataIndex];
                            
                            // If N/A, return just 'N/A' without field-specific formatting
                            if (originalValue === 'N/A') return 'N/A';
                            
                            if (percentageDbFields.includes(tile.field)) return `${value.toFixed(1)}%`
                            if (amountDbFields.includes(tile.field)) return `$${value.toFixed(1)}`
                            return `${value.toFixed(1)}x`
                        }  
                    },
                    tooltip: {
                        footerFont: { weight: 'normal' },   // library defaults the footer to bold
                        callbacks: {
                            label: function(context) {
                                const percentageDbFields = ["ttmRevenueGrowth", "ttmEbitdaMargin", "premiumPaid"]
                                const amountDbFields = ["dealSize"]
                                
                                // Get original value from dbValuesForField (preserves N/A)
                                const originalValue = dbValuesForField[context.dataIndex];
                                
                                // If N/A, return just 'N/A' without field-specific formatting
                                if (originalValue === 'N/A') return 'N/A';
                                
                                const value = context.parsed.y
                                if (percentageDbFields.includes(tile.field)) return `${value.toFixed(1)}%`
                                if (amountDbFields.includes(tile.field)) return `$${value.toFixed(1)}B`
                                return `${value.toFixed(1)}x`
                            },
                            // afterBody (rather than afterLabel) so the companion line renders flush left, visually separated from the hovered value rather than indented under its color box
                            afterBody: function(tooltipItems) {
                                // **--------- Returns a string to get rendered into the afterBody slot in the tooltip scaffolding provided by the library ----------** //
                                //┌─────────────────────────┐
                                //│ beforeTitle             │
                                //│ title         (bold)    │  title section, once per tooltip
                                //│ afterTitle              │
                                //│                         │
                                //│ beforeBody              │  once per tooltip, flush left
                                //│   beforeLabel           │
                                //│ ■ label                 │  once per hovered item, indented with svg beforehand
                                //│   afterLabel            │
                                //│ afterBody     ← yours   │  once per tooltip, flush left
                                //│                         │
                                //│ beforeFooter            │
                                //│ footer        (bold)    │  footer section, once per tooltip
                                //│ afterFooter             │
                                //└─────────────────────────┘                         
                                
                                // **---------- Tooltip Companion: For Some Tile's We Are Going To Display a 'Companion Field's' Value As Additional Data in the Tooltip for That Company's Bar ----------** //
                                const barIndex = tooltipItems[0].dataIndex               
                                return orchestrateCompanion(TC_TOOLTIP_TILE_COMPANION_DICTIONARY, tile, orderedIds, barIndex, transactions) 
                            },
                            footer: function(tooltipItems) {
                                // **---------- Tooltip Footer: Acquirer and Target Names of the Bar Being Hovered ----------** //
                                const barIndex = tooltipItems[0].dataIndex
                                return orchestrateFooterCompanion("transaction", orderedIds, barIndex, transactions)
                            }
                        }
                    }
                    /* 
                    add tooltip prop here   
                    once we finish rest of code and decide how we want to display txns
                    i.e. if we want both acquirer and target logos + year? Just both logos? just target logo and year, etc
                    */
                },
                scales: {
                    y: {
                        afterFit: (scale) => { scale.width = isViewportWidthMobileBrowser ? Y_AXIS_GUTTER_MOBILE_PX : Y_AXIS_GUTTER_PX }, // pins the gutter so the plot area's left edge is a constant across metrics, which is what the logo row's margin-left keys off of; mobile gets a narrower one since the tick font drops to 10
                        min: orchestrateYAxis(dbValuesForField).min, // change this to pass a "min" / "max" key into each, so we can show which we are running in logs
                        max: orchestrateYAxis(dbValuesForField).max, // change this to pass a "min" / "max" key into each, so we can show which we are running in logs
                        grid: {
                            display: false
                        },
                        ticks: {
                            font: {
                                size: isViewportWidthMobileBrowser ? 10 : 11
                            },
                            precision: 0,        // calls to .toFixed(0) internally on each index value (tick value); which rounds tick index value to the nearest integer
                            callback: function(value, index, ticks) {
                                const isFirst  = index === 0;                            // returns true or false
                                const isMiddle = index === Math.floor(ticks.length / 2); // returns true or false
                                const isLast   = index === ticks.length - 1;             // returns true or false

                                const percentageDbFields = ["ttmRevenueGrowth", "ttmEbitdaMargin", "premiumPaid"]
                                const amountDbFields     = ["dealSize"]

                                if(isFirst || isMiddle || isLast ) {
                                    if (percentageDbFields.includes(tile.field)) return `${value}%`
                                    if (amountDbFields.includes(tile.field))     return `$${value}B`
                                    return `${value}x`
                                }
                                return ""
                            }
                        },
                    },
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            display: false
                        }
                    }
                },
                layout: {
                    padding: {
                        top: 30
                    }
                }
            }
        });
    };

    const toggleTransactionSelection = (transactionId) => {
        if (selectedTransactionIds.includes(transactionId)) {
            onTransactionSelectionChange(selectedTransactionIds.filter(id => id !== transactionId));
        } else if (selectedTransactionIds.length < SELECTION_UPPER_LIMIT) {
            onTransactionSelectionChange([...selectedTransactionIds, transactionId]);
        }
    };

    const removeTransactionSelection = (transactionId) => {
        onTransactionSelectionChange(selectedTransactionIds.filter(id => id !== transactionId));
    };

    const removeTileFromTray = (index) => {
        const newSelectedTiles = [...selectedTransTileIds];
        newSelectedTiles[index] = null;
        onTransTileSelectionChange(newSelectedTiles);
    };

    const filteredTransactions = transactions.filter(transaction => 
        transaction.acquirerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.targetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.acquirerTicker.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.targetTicker.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const displayedTransactions = transactions.filter(transaction => 
        selectedTransactionIds.includes(transaction.id)
    );

    return (
        <div className="page-wrapper">
            <div className="dashboard">
            {/* Left Panel - Controls */}
                <div className="left-panel">
                    {/* Header Section */}
                    <div className="left-panel-header-container">
                        <h2 className="panel-title">Transaction Metrics</h2>
                    </div>
                    
                    {/* Body Section: Metric Control Panel */}

                    {/* Body Section 1: Component Trays for dropping metric tiles from metric palette */}
                    <div className="component-tray-container">
                        {[0, 1].map(index => (
                            <div
                                key={index}
                                className={`component-tray ${selectedTransTileIds[index] ? 'occupied' : ''} ${draggedTileId === selectedTransTileIds[index] ? 'dragging' : ''}`}
                                data-tray-index={index}
                                draggable={selectedTransTileIds[index] ? true : false}
                                onDragStart={selectedTransTileIds[index] === null ? null : (e) => handleDragStart(e, selectedTransTileIds[index], index)}                                
                                onDragOver={handleDragOver}
                                onDrop={(e) => handleTrayDrop(e, index)}
                                // Mouse family side Optional: just helpful for UI state updates for user when entering and leaving this element with a dragged item
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}

                                onTouchStart={selectedTransTileIds[index] === null ? undefined : (e) => handleTouchStart(e, selectedTransTileIds[index], index)}
                                onTouchMove={selectedTransTileIds[index] === null ? undefined : handleTouchMove}
                                onTouchEnd={selectedTransTileIds[index] === null ? undefined : handleTouchEnd}
                            >
                                {selectedTransTileIds[index] === null ? (
                                    <div className="component-tray-text">
                                        Drop metric {index + 1} here
                                    </div>
                                ) : (
                                    <div className="component-tray-text">
                                        {findOneById(transTiles, selectedTransTileIds[index]).name}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Body Section 2: Metric Pallette with Tiles to Drop into Trays */}
                    <div className="transaction-tiles-container">
                        <div 
                            className="tiles-grid"
                            onDragOver           =   {handleDragOver}
                            onDrop               =   {handlePaletteDrop}
                            onDragEnter          =   {handleDragEnter}
                            onDragLeave          =   {handleDragLeave}
                        >
                            <MetricPaletteSections
                               sections          =    {TRANS_TILE_SECTIONS_LIST}
                               tiles             =    {transTiles}
                               selectedTileIds   =    {selectedTransTileIds}                                   
                               draggedTileId     =    {draggedTileId}
                               handleDragStart   =    {handleDragStart}
                               handleTouchStart  =    {handleTouchStart}
                               handleTouchMove   =    {handleTouchMove}
                               handleTouchEnd    =    {handleTouchEnd}
                            />                                                          
                        </div>
                    </div>
                        
                </div>

                {/* Right Panel - Charts */}
                <div className="right-panel">

                    {/* Header Section */}
                    <div className="right-panel-header-container">
                        <h2 className="panel-title">Transaction Profiles</h2>
                        <div className="right-panel-benchmarking-charts-controls-container">
                            <button
                                className="selector-btn"
                                onClick={() => setIsOffcanvasOpen(true)}
                            >
                                <svg className="edit-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit Selection
                            </button>
                        </div>
                    </div>
                        
                    {/* Charting Section */} 
                    <div className="charts-container">
                        
                        {/* Chart 1 */}
                        {selectedTransTileIds[0] ? (
                            <div className="chart-section">
                                <h3 className="chart-title">{findOneById(transTiles, selectedTransTileIds[0]).name}</h3>
                                <div className="chart-container">
                                    <canvas ref={chart1Ref}></canvas>
                                </div>
                                {/* print logos corresponding to bars in chart */}
                                <div className="charted-transactions-logo-container">
                                    {displayedTransactions
                                        .sort((a, b) => b[findOneById(transTiles, selectedTransTileIds[0]).field] - a[findOneById(transTiles, selectedTransTileIds[0]).field])
                                        .map((transaction) => (
                                            <div key={transaction.id} className="charted-transaction-logo-card">
                                                <div className="transaction-logo-pair-container">
                                                    <img 
                                                        className="logo-card-company-logo"
                                                        src={transaction.acquirerLogo === "API" ? getCoLogo(transaction.acquirerTicker) : transaction.acquirerLogo}
                                                        alt={transaction.acquirerTicker}
                                                        title={transaction.acquirerName}
                                                    />
                                                    <span className="acquisition-arrow">→</span>
                                                    <img 
                                                        className="logo-card-company-logo"
                                                        // truthy path from ternary: value is "API" so call the logo API to fetch the image
                                                        // falsy path from ternary: value starts with / (Root-Relative Path) to use local asset from /public folder
                                                        src={ transaction.targetLogo === "API" ? getCoLogo(transaction.targetTicker) : transaction.targetLogo } 
                                                        alt={transaction.targetTicker}
                                                        title={transaction.targetName}
                                                    />
                                                </div>
                                                <div className="transaction-year-row">
                                                    {transaction.year}
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        ) : (
                            <div className="transaction-chart-wrapper">
                                <div className="transaction-empty-state">
                                    First metric tile from palette charted here
                                </div>
                            </div>
                        )}

                        {/* Chart 2 */} 
                        {selectedTransTileIds[1] ? (
                            <div className="chart-section">
                                <h3 className="chart-title">{findOneById(transTiles, selectedTransTileIds[1]).name}</h3>
                                <div className="chart-container">
                                    <canvas ref={chart2Ref}></canvas>
                                </div>
                                {/* print logos corresponding to bars in chart */}
                                <div className="charted-transactions-logo-container">
                                    {displayedTransactions
                                        .sort((a, b) => b[findOneById(transTiles, selectedTransTileIds[1]).field] - a[findOneById(transTiles, selectedTransTileIds[1]).field])
                                        .map((transaction) => (
                                            <div key={transaction.id} className="charted-transaction-logo-card">
                                                <div className="transaction-logo-pair-container">
                                                    <img 
                                                        className="logo-card-company-logo"
                                                        src={transaction.acquirerLogo === "API" ? getCoLogo(transaction.acquirerTicker) : transaction.acquirerLogo}
                                                        alt={transaction.acquirerTicker}
                                                        title={transaction.acquirerName}
                                                    />
                                                    <span className="acquisition-arrow">→</span>
                                                    <img 
                                                        className="logo-card-company-logo"
                                                        src={transaction.targetLogo === "API" ? getCoLogo(transaction.targetTicker) : transaction.targetLogo}
                                                        alt={transaction.targetTicker}
                                                        title={transaction.targetName}
                                                    />
                                                </div>
                                                <div className="transaction-year-row">
                                                    {transaction.year}
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        ) : (
                            <div className="transaction-chart-wrapper">
                                <div className="transaction-empty-state">
                                    Second metric tile from palette charted here
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Offcavas Section */}
                {/* 3. Offcanvas Overlay */}
                <div 
                    className={`transaction-backdrop ${isOffcanvasOpen ? 'show' : ''}`}
                    onClick={() => setIsOffcanvasOpen(false)}
                ></div>

                {/* 4. Transaction Selector Offcanvas */}
                <div className={`transaction-offcanvas ${isOffcanvasOpen ? 'open' : ''}`}>
                    
                    {/* Header Section */}
                    <div className="transaction-offcanvas-header">
                        <h3 className="transaction-offcanvas-title">Select Transactions</h3>
                        <button 
                            className="transaction-close-btn"
                            onClick={() => setIsOffcanvasOpen(false)}
                        >
                            ×
                        </button>
                    </div>
                    
                    {/* Body Section */}
                    <div className="transaction-offcanvas-body">

                        {/* Sector Dropdown and List Items */}
                        <div className="sector-dropdown-container">
                            <div className="sector-dropdown">
                                <button 
                                    className="sector-dropdown-button"
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <div className="dropdown-content">
                                        <span className="dropdown-label">Select Sector</span>
                                        <div className="dropdown-value-wrapper">
                                            <div className="dropdown-button-icon"></div>
                                            <span className="dropdown-value">{findOneById(transCategories, selectedTransCategoryId)?.name || 'Choose an option'}</span>
                                        </div>
                                    </div>
                                    <svg className={`dropdown-chevron ${isDropdownOpen ? 'open' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                {isDropdownOpen && (
                                    <div className="sector-dropdown-menu">
                                        {transCategories.map((sector, index) => (
                                            <div 
                                                key={sector.id}
                                                className={`sector-dropdown-item ${selectedTransCategoryId === sector.id ? 'selected' : ''}`}
                                                onClick={() => {
                                                    onTransCategorySelectionChange(sector.id);
                                                    setSearchTerm("")
                                                    setIsDropdownOpen(false);
                                                }}
                                            >
                                                {index < 2 && <div className="sector-icon"> </div>}
                                                <span className="sector-name">{sector.name}</span>
                                                {selectedTransCategoryId === sector.id && (
                                                    <svg className="checkmark-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                            
                        {/* Search */}
                        <div className="search-container">
                            <div className="search-input-wrapper">
                                <div className="search-content">
                                    <span className="search-label">Transaction Search</span>
                                    <div className="dropdown-value-wrapper">
                                        <div className="search-icon"></div>
                                        <input
                                            type="text"
                                            className="search-input-field"
                                            placeholder="Search sector for precedent transaction"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                            
                        {/* Selected Transactions Section */}
                        {selectedTransactionIds.length > 0 && (
                            <div className="selected-transactions-section">
                                <h4 className="selected-transactions-title">
                                    Selected Transactions ({selectedTransactionIds.length}/{SELECTION_UPPER_LIMIT})
                                </h4>
                                <div className="selected-transactions-grid-container">
                                    {filterManyByIds(transactions, selectedTransactionIds).map(tx => (
                                        <div key={tx.id} className="selected-transaction-logo-card">
                                            <div className="transaction-logo-pair-container">
                                                <img 
                                                    src={tx.acquirerLogo === "API" ? getCoLogo(tx.acquirerTicker) : tx.acquirerLogo}
                                                    alt={tx.acquirerName}
                                                    className="logo-card-company-logo"
                                                />
                                                <span className="acquisition-arrow">→</span>
                                                <img 
                                                    src={tx.targetLogo === "API" ? getCoLogo(tx.targetTicker) : tx.targetLogo}
                                                    alt={tx.targetName}
                                                    className="logo-card-company-logo"
                                                />
                                            </div>
                                            <div className="selected-transaction-info">
                                                <span className="selected-transaction-name">
                                                    {tx.targetName}
                                                </span>
                                                <span className="selected-transaction-details">
                                                    {tx.acquirerName} • {tx.year}
                                                </span>
                                            </div>
                                            <button 
                                                className="remove-transaction-btn"
                                                onClick={() => removeTransactionSelection(tx.id)}
                                                title="Remove transaction"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Available Transactions to Select Grid */}
                        <div className="available-transactions-grid-container">
                            {filteredTransactions.map(transaction => (
                                <div
                                    key={transaction.id}
                                    className={`available-transaction-logo-card ${
                                        selectedTransactionIds.includes(transaction.id) ? 'selected' : ''
                                    }`}
                                    onClick={() => toggleTransactionSelection(transaction.id)}
                                >
                                    <div className="transaction-logo-pair-container">
                                        <img 
                                            src={transaction.acquirerLogo === "API" ? getCoLogo(transaction.acquirerTicker) : transaction.acquirerLogo}
                                            alt={transaction.acquirerName}
                                            className="logo-card-company-logo"
                                        />
                                        <span className="acquisition-arrow">→</span>
                                        <img 
                                            src={transaction.targetLogo === "API" ? getCoLogo(transaction.targetTicker) : transaction.targetLogo}
                                            alt={transaction.targetName}
                                            className="logo-card-company-logo"
                                        />
                                    </div>
                                    <div className="transaction-card-title">{transaction.targetName}</div>
                                    <div className="transaction-card-details">Acquired by {transaction.acquirerName}</div>
                                    <div className="transaction-card-details">{transaction.year}</div>
                                    <div className="transaction-card-value">
                                        ${transaction.dealSize}B Deal Size
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ghost element for touch drag - only renders during touch drag */}
            {draggedTileId && ghostPosition && (
                <div 
                    className="ghost-tile"
                    style={{
                        left: `${ghostPosition.x}px`,
                        top: `${ghostPosition.y}px`
                    }}
                >
                    {transTiles.find(t => t.id === draggedTileId)?.name || ''}
                </div>
            )}
        </div>
    );
};

export default TransactionComps;
